import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import {
  Alert, Box, Chip, CircularProgress, Divider, Drawer, IconButton,
  InputAdornment, LinearProgress, MenuItem, Pagination, Stack, Table,
  TableBody, TableCell, TableHead, TableRow, TextField, Tooltip,
  Typography, alpha, useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

import {
  fetchRequest as fetchLeaderboard,
} from '../../redux/actions/discovery/fleetLeaderboard';
import {
  fetchRequest as fetchMember, clearState as clearMember,
} from '../../redux/actions/discovery/fleetMember';
import useRealtimeRefresh, { eventPrefix } from '../../realtime/useRealtimeRefresh';
import { useDisplayNames } from '../../utils/displayNames';

const PAGE_SIZE = 50;

const fmtScore = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n >= 0.995 || n === 0 ? n.toFixed(2) : n.toFixed(3).replace(/^0/, '');
};

const fmtGrowth = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  const pct = n * 100;
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct.toFixed(1)}%`;
};

const fmtWhen = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch (e) { return null; }
};

// Movement cell: ▲n climbed, ▼n slid, NEW for first appearance.
const Movement = ({ row }) => {
  const theme = useTheme();
  if (!row.prev_rank) {
    return (
      <Tooltip title="First appearance on the board" arrow>
        <FiberNewIcon sx={{ fontSize: 18, color: theme.palette.info.main }} />
      </Tooltip>
    );
  }
  const d = Number(row.rank_delta) || 0;
  if (d === 0) return <Typography variant="caption" color="text.disabled">—</Typography>;
  const up = d > 0; // positive delta = climbed (prev_rank - rank)
  const color = up ? theme.palette.error.main : theme.palette.success.main;
  const Icon = up ? ArrowUpwardIcon : ArrowDownwardIcon;
  return (
    <Tooltip title={`${up ? 'Climbed' : 'Slid'} ${Math.abs(d)} place${Math.abs(d) === 1 ? '' : 's'} since the previous run`} arrow>
      <Stack direction="row" spacing={0.25} alignItems="center" sx={{ color }}>
        <Icon sx={{ fontSize: 14 }} />
        <Typography variant="caption" fontWeight={700}>{Math.abs(d)}</Typography>
      </Stack>
    </Tooltip>
  );
};

// Fleet-health strip: the score distribution as a tiny fixed-bin histogram.
const HistogramStrip = ({ histogram }) => {
  const theme = useTheme();
  if (!histogram?.length) return null;
  const max = Math.max(...histogram.map((b) => b.count), 1);
  return (
    <Tooltip title="How scores are distributed across the fleet — a long right tail means a few members stand far out." arrow>
      <Stack direction="row" spacing={0.25} alignItems="flex-end" sx={{ height: 34, cursor: 'help' }}>
        {histogram.map((b) => (
          <Box
            key={b.bucket}
            sx={{
              flex: 1,
              minWidth: 6,
              height: `${Math.max(8, (b.count / max) * 100)}%`,
              borderRadius: '2px 2px 0 0',
              // Growth-fleet buckets can sit outside [0, 1]; clamp the tint.
              bgcolor: alpha(theme.palette.primary.main, Math.min(0.85, Math.max(0.15, 0.25 + 0.6 * b.bucket))),
            }}
          />
        ))}
      </Stack>
    </Tooltip>
  );
};

// Tiny inline SVG sparkline (drawer history + recent context).
const Sparkline = ({ values, color, height = 48 }) => {
  const theme = useTheme();
  const W = 360;
  const stroke = color || theme.palette.primary.main;
  const pts = (values || []).filter((v) => Number.isFinite(Number(v))).map(Number);
  if (pts.length < 2) {
    return <Typography variant="caption" color="text.disabled">Not enough data yet.</Typography>;
  }
  let lo = Math.min(...pts);
  let hi = Math.max(...pts);
  if (lo === hi) { lo -= 1; hi += 1; }
  const x = (i) => (i / (pts.length - 1)) * (W - 4) + 2;
  const y = (v) => height - 4 - ((v - lo) / (hi - lo)) * (height - 8);
  const d = pts.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  return (
    <svg width="100%" viewBox={`0 0 ${W} ${height}`} style={{ display: 'block' }}>
      <path d={d} fill="none" stroke={stroke} strokeWidth={1.6} />
    </svg>
  );
};

const MemberDrawer = ({ goalId, memberKey, onClose, member, memberFetch, memberClear }) => {
  const theme = useTheme();
  const nameOf = useDisplayNames();

  useEffect(() => {
    if (memberKey) memberFetch({ goalId, memberKey });
    return () => memberClear();
  }, [goalId, memberKey, memberFetch, memberClear]);

  const data = member?.response;
  // History arrives newest-first; sparklines read left→right in time.
  const history = useMemo(() => [...(data?.history || [])].reverse(), [data]);
  const latest = history[history.length - 1];

  return (
    <Drawer anchor="right" open={Boolean(memberKey)} onClose={onClose}>
      <Box sx={{ width: { xs: '100vw', sm: 420 }, p: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle1" fontWeight={700} noWrap>
              {nameOf(memberKey)}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
              {memberKey}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
        </Stack>
        <Divider sx={{ mb: 1.5 }} />

        {member?.isLoading && (
          <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress size={22} /></Stack>
        )}
        {member?.error && <Alert severity="error">{String(member.error)}</Alert>}

        {data && (
          <Stack spacing={2}>
            {latest && (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip size="small" color="primary" label={`Rank #${latest.rank}`} />
                <Chip size="small" variant="outlined" label={`Score ${fmtScore(latest.score)}`} />
                {latest.prev_rank ? (
                  <Chip
                    size="small" variant="outlined"
                    label={`${latest.rank_delta > 0 ? '▲' : latest.rank_delta < 0 ? '▼' : '—'} ${Math.abs(latest.rank_delta) || 'steady'}`}
                  />
                ) : <Chip size="small" variant="outlined" color="info" label="New entry" />}
              </Stack>
            )}

            <Box>
              <Typography variant="overline" color="text.secondary">Score across runs</Typography>
              <Sparkline values={history.map((h) => h.score)} />
            </Box>

            <Box>
              <Typography variant="overline" color="text.secondary">Rank across runs</Typography>
              {/* Rank axis is inverted mentally (1 = best); negate so "up" draws up. */}
              <Sparkline
                values={history.map((h) => -h.rank)}
                color={theme.palette.secondary.main}
              />
            </Box>

            <Box>
              <Typography variant="overline" color="text.secondary">Recent signal</Typography>
              <Sparkline values={data.recent_values} color={theme.palette.success.main} />
            </Box>

            {latest && (
              <Box sx={{ p: 1.25, borderRadius: 2, border: `1px dashed ${theme.palette.divider}` }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Latest run detail
                </Typography>
                <Typography variant="body2">
                  Forecast summary {fmtScore(latest.point_summary)} · baseline {fmtScore(latest.baseline)}
                  {Number(latest.threshold) ? ` · threshold ${fmtScore(latest.threshold)}` : ''}
                </Typography>
              </Box>
            )}
          </Stack>
        )}
      </Box>
    </Drawer>
  );
};

const FleetLeaderboardPanel = ({
  goalId, goal,
  board, boardFetch,
  member, memberFetch, memberClear,
}) => {
  const theme = useTheme();
  const nameOf = useDisplayNames();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [order, setOrder] = useState('rank');
  const [moversOnly, setMoversOnly] = useState(false);
  const [openMember, setOpenMember] = useState(null);

  const load = React.useCallback(() => {
    boardFetch({
      goalId,
      offset: (page - 1) * PAGE_SIZE,
      limit: PAGE_SIZE,
      search: search.trim() || undefined,
      moversOnly,
      order,
    });
  }, [boardFetch, goalId, page, search, moversOnly, order]);

  useEffect(() => {
    const t = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  // Live board: refetch when a run completes or progresses (debounced).
  useRealtimeRefresh(
    eventPrefix('goal.fleet.run.'),
    () => load(),
  );

  const data = board?.response;
  const run = data?.run;
  const rows = data?.rows || [];
  const total = data?.total || 0;
  const stats = run?.stats;
  const isRisk = goal?.goal_type === 'rank_by_risk';
  const scoreHeader = isRisk ? 'Breach risk' : 'Predicted growth';

  const fleetState = (goal?.fleet) || {};
  const bakeoffPending = !fleetState.model;

  if (bakeoffPending) {
    return (
      <Alert severity="info" icon={<EmojiEventsIcon fontSize="small" />}>
        Bake-off is choosing the champion — the leaderboard appears when it finishes.
      </Alert>
    );
  }

  return (
    <Stack spacing={1.5}>
      <Box>
        <Typography variant="subtitle2" fontWeight={700}>
          {isRisk ? 'Ranked by breach risk' : 'Ranked by predicted growth'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {isRisk
            ? 'Each member’s chance of crossing its own threshold over the horizon. Higher is riskier.'
            : 'Predicted change vs each member’s recent level over the horizon. +5.1% means that member is expected to grow 5.1%.'}
        </Typography>
      </Box>
      {/* Run summary strip */}
      {run && (
        <Box
          sx={{
            display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center',
            p: 1.5, borderRadius: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.04),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary">Last scored</Typography>
            <Typography variant="body2" fontWeight={700}>{fmtWhen(run.finished_at) || '…'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Members</Typography>
            <Typography variant="body2" fontWeight={700}>
              {(run.members_scored ?? 0).toLocaleString()} / {(run.members_total ?? 0).toLocaleString()}
            </Typography>
          </Box>
          {stats && (
            <>
              <Box>
                <Typography variant="caption" color="text.secondary">Median score</Typography>
                <Typography variant="body2" fontWeight={700}>{fmtScore(stats.score_p50)}</Typography>
              </Box>
              <Box>
                <Tooltip title="Members that newly entered the top decile this run" arrow>
                  <Box sx={{ cursor: 'help' }}>
                    <Typography variant="caption" color="text.secondary">New in top 10%</Typography>
                    <Typography variant="body2" fontWeight={700}>{stats.entered_top_decile}</Typography>
                  </Box>
                </Tooltip>
              </Box>
            </>
          )}
          {run.model?.family && (
            <Chip size="small" variant="outlined" label={`${run.model.slug || run.model.family}`} sx={{ ml: 'auto' }} />
          )}
          <Box sx={{ minWidth: 160, flex: '0 1 220px' }}>
            <HistogramStrip histogram={data?.histogram} />
          </Box>
        </Box>
      )}

      {/* An in-flight run renders as a progress bar above the last board. */}
      {run && ['queued', 'running', 'reducing'].includes(run.status) && (
        <LinearProgress sx={{ borderRadius: 1 }} />
      )}

      {/* Controls */}
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <TextField
          size="small"
          placeholder="Find a member…"
          value={search}
          onChange={(e) => { setPage(1); setSearch(e.target.value); }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
            ),
          }}
          sx={{ width: 240 }}
        />
        <TextField
          select size="small" value={order}
          onChange={(e) => { setPage(1); setOrder(e.target.value); }}
          sx={{ width: 170 }}
        >
          <MenuItem value="rank">Best score first</MenuItem>
          <MenuItem value="movement">Biggest climbers first</MenuItem>
        </TextField>
        <Chip
          size="small"
          label="Movers only"
          color={moversOnly ? 'primary' : 'default'}
          variant={moversOnly ? 'filled' : 'outlined'}
          onClick={() => { setPage(1); setMoversOnly((v) => !v); }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          {total.toLocaleString()} member{total === 1 ? '' : 's'}
        </Typography>
      </Stack>

      {board?.error && <Alert severity="error">{String(board.error)}</Alert>}

      {board?.isLoading && !rows.length ? (
        <Stack alignItems="center" sx={{ py: 5 }}><CircularProgress size={24} /></Stack>
      ) : !rows.length ? (
        <Alert severity="info">
          {run
            ? 'No members match these filters.'
            : 'No completed scoring run yet — the first leaderboard appears within minutes of the bake-off finishing.'}
        </Alert>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 56 }}>Rank</TableCell>
                <TableCell sx={{ width: 64 }}>Move</TableCell>
                <TableCell>Member</TableCell>
                <TableCell sx={{ width: 220 }}>{scoreHeader}</TableCell>
                <TableCell align="right">Forecast</TableCell>
                <TableCell align="right">Baseline</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const score = Number(row.score) || 0;
                const barPct = isRisk
                  ? Math.min(100, Math.max(0, score * 100))
                  : Math.min(100, Math.abs(score));
                const topDecile = stats?.top_decile_size
                  ? row.rank <= stats.top_decile_size : false;
                return (
                  <TableRow
                    key={row.member_key}
                    hover
                    onClick={() => setOpenMember(row.member_key)}
                    sx={{
                      cursor: 'pointer',
                      ...(topDecile ? { bgcolor: alpha(theme.palette.warning.main, 0.05) } : {}),
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={row.rank <= 3 ? 800 : 500}>
                        {row.rank <= 3 && <EmojiEventsIcon sx={{ fontSize: 13, mr: 0.25, color: theme.palette.warning.main, verticalAlign: 'text-top' }} />}
                        {row.rank}
                      </Typography>
                    </TableCell>
                    <TableCell><Movement row={row} /></TableCell>
                    <TableCell sx={{ maxWidth: 320 }}>
                      <Typography variant="body2" noWrap>{nameOf(row.member_key)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: alpha(theme.palette.primary.main, 0.1), overflow: 'hidden' }}>
                          <Box sx={{
                            width: `${barPct}%`, height: '100%',
                            bgcolor: isRisk
                              ? (score > 0.66 ? theme.palette.error.main
                                : score > 0.33 ? theme.palette.warning.main
                                  : theme.palette.success.main)
                              : theme.palette.primary.main,
                          }} />
                        </Box>
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          sx={{
                            width: 56, textAlign: 'right',
                            color: isRisk
                              ? undefined
                              : (score > 0 ? theme.palette.success.main : score < 0 ? theme.palette.error.main : undefined),
                          }}
                        >
                          {isRisk ? `${Math.round(score * 100)}%` : fmtGrowth(score)}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="caption">{fmtScore(row.point_summary)}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="caption" color="text.secondary">{fmtScore(row.baseline)}</Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}

      {total > PAGE_SIZE && (
        <Stack alignItems="center">
          <Pagination
            size="small"
            count={Math.ceil(total / PAGE_SIZE)}
            page={page}
            onChange={(_e, p) => setPage(p)}
          />
        </Stack>
      )}

      <MemberDrawer
        goalId={goalId}
        memberKey={openMember}
        onClose={() => setOpenMember(null)}
        member={member}
        memberFetch={memberFetch}
        memberClear={memberClear}
      />
    </Stack>
  );
};

const mapStateToProps = (state) => ({
  board: state.discovery_fleet_leaderboard,
  member: state.discovery_fleet_member,
});

const mapDispatchToProps = (dispatch) => ({
  boardFetch: (payload) => dispatch(fetchLeaderboard(payload)),
  memberFetch: (payload) => dispatch(fetchMember(payload)),
  memberClear: () => dispatch(clearMember()),
});

export default connect(mapStateToProps, mapDispatchToProps)(FleetLeaderboardPanel);
